import { Shield, Users, User } from "lucide-react";
import { storyElement } from "@/lib/story-element";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";

interface ReputationWidgetProps {
  variables?: Record<string, any> | null;
  compact?: boolean;
}

function reputationLabel(val: number, scale: string): string {
  if (scale === "simple") {
    const labels: Record<number, string> = {
      [-3]: "Hated", [-2]: "Hostile", [-1]: "Unfriendly",
      0: "Neutral", 1: "Friendly", 2: "Honored", 3: "Revered",
    };
    return labels[val] ?? `${val}`;
  }
  if (val <= -75) return "Hated";
  if (val <= -40) return "Hostile";
  if (val <= -10) return "Unfriendly";
  if (val <= 10) return "Neutral";
  if (val <= 40) return "Friendly";
  if (val <= 75) return "Honored";
  return "Revered";
}

function suspicionLabel(val: number): string {
  if (val <= 20) return "Trusting";
  if (val <= 40) return "Friendly";
  if (val <= 60) return "Guarded";
  if (val <= 80) return "Suspicious";
  return "Hostile";
}

export function ReputationWidget({ variables, compact }: ReputationWidgetProps) {
  if (!variables) return null;

  const repEntries = Object.entries(variables).filter(([k]) => k.startsWith("reputation_"));
  const npcSuspicion = variables._npc_suspicion || {};
  const npcEntries = Object.entries(npcSuspicion).filter(([, v]) => typeof v === "number");
  const scale = variables._reputation_scale || "simple";
  const max = scale === "simple" ? 3 : 100;
  const min = scale === "simple" ? -3 : -100;

  const hasData = repEntries.length > 0 || npcEntries.length > 0;

  if (!hasData) {
    return (
      <div className="text-xs text-muted-foreground/50 text-center py-4">
        No reputation data yet
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <Shield className="w-3 h-3 text-muted-foreground" />
        <span className="text-[10px] text-muted-foreground">
          {repEntries.length} faction{repEntries.length !== 1 ? "s" : ""}
          {npcEntries.length > 0 && `, ${npcEntries.length} NPC${npcEntries.length !== 1 ? "s" : ""}`}
        </span>
      </div>
    );
  }

  return (
    <div {...storyElement("reputation-widget")} className="space-y-4">
      {/* Faction Reputation */}
      {repEntries.length > 0 && (
        <div className="space-y-3">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
            <Users className="w-3 h-3" />Factions
          </div>
          {repEntries.map(([key, val]) => {
            const faction = key.replace("reputation_", "").replace(/_/g, " ");
            const numVal = Number(val) || 0;
            const label = reputationLabel(numVal, scale);
            const normalized = ((numVal - min) / (max - min)) * 100;
            const isNegative = numVal < 0;

            return (
              <div key={key} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium capitalize truncate">{faction}</span>
                  <span className={cn(
                    "text-[10px]",
                    isNegative ? "text-destructive" : numVal > 0 ? "text-green-400" : "text-muted-foreground",
                  )}>
                    {label} ({numVal > 0 ? "+" : ""}{numVal})
                  </span>
                </div>
                <Progress value={normalized} className="h-1.5" />
              </div>
            );
          })}
        </div>
      )}

      {/* NPC Disposition */}
      {npcEntries.length > 0 && (
        <>
          {repEntries.length > 0 && <Separator />}
          <div className="space-y-3">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
              <User className="w-3 h-3" />NPC Disposition
            </div>
            {(npcEntries as [string, number][]).map(([npc, level]) => {
              const label = suspicionLabel(level);
              const isBad = level > 60;
              const isGood = level <= 40;

              return (
                <div key={npc} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium capitalize truncate">{npc.replace(/_/g, " ")}</span>
                    <span className={cn(
                      "text-[10px]",
                      isBad ? "text-destructive" : isGood ? "text-green-400" : "text-muted-foreground",
                    )}>
                      {label} ({level})
                    </span>
                  </div>
                  <Progress value={100 - level} className="h-1.5" />
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
